import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { EmailSendingAddress, EmailLayout, EmailTemplate } from '../../email/EmailTypes';
import { Division } from '../../environments/EnvironmentTypes';
import { connectedFeatureOptions, triggerOffsetUnitOptions, triggerStatusOptionsMap, triggerTimingOptions } from './emailTemplateFormOptions';
import { normalizeString } from 'services/utils/normalizeString';
import Dropdown from 'components/forms/basefields/Dropdown';
import { useSettings } from 'services/settings/SettingsContext';

interface EmailTemplateFormBaseFieldsProps {
    data: EmailTemplate | null;
    loading: 'idle' | 'show-loader' | 'success';
    divisions: Division[];
    sendingAddresses: EmailSendingAddress[];
    layouts: EmailLayout[];
    templateType?: string;
    setData: React.Dispatch<React.SetStateAction<EmailTemplate | null>>;
    setSelectedSendTo: React.Dispatch<React.SetStateAction<string[]>>;
    setFilteredSendingAddresses: React.Dispatch<React.SetStateAction<EmailSendingAddress[]>>;
}

const EmailTemplateFormBaseFields: React.FC<EmailTemplateFormBaseFieldsProps> = ({
    data, loading, divisions, sendingAddresses, layouts, templateType,
    setData, setSelectedSendTo, setFilteredSendingAddresses
}) => {
    const { t } = useTranslation();
    const { environmentSettings } = useSettings();
    const { errorMessages } = useGlobalContext();
    const { activeFeatures, roleFeatures, allowedFeatures } = useAuthContext();
    const [filteredEmailLayouts, setFilteredEmailLayouts] = useState<EmailLayout[]>([]);

    // Filter the layouts when a division is selected
    useEffect(() => {
        if (layouts) {
            // Show all layouts when no division is selected
            if (!data?.division) {
                const allLayouts = layouts.filter(layout => !layout.deleted);
                setFilteredEmailLayouts(allLayouts);
                return;
            }

            // Filter layouts when a division is selected
            const divisionLayouts = layouts.filter(layout =>
                !layout.deleted && (
                    layout.division === data.division ||
                    layout.division == null
                )
            );

            setFilteredEmailLayouts(divisionLayouts);
        }
    }, [layouts, data?.division]);

    // Filter the sending addresses or set the default from email
    useEffect(() => {
        if (sendingAddresses && divisions && environmentSettings && data) {

            // Get the selected division object and main division object
            const selectedDivisionObject = data.division ? divisions.find(division => division.id === data.division) : null;
            const mainDivisionObject = divisions.find(division => division.is_main && division.is_active);

            // Use the selected division or fallback to the main division object
            const effectiveDivisionObject = selectedDivisionObject || mainDivisionObject || null;

            // Generate the default from email
            const defaultFromPrefix = normalizeString(effectiveDivisionObject?.name || 'no-reply');
            const defaultFromEmail = `${defaultFromPrefix}@deliver.getcrafter.com`;

            // For the default email domain method, use the fallback email
            if (environmentSettings.email_domain_method === 'default') {
                setFilteredSendingAddresses([]);

                setData(prev => ({
                    ...prev!,
                    from_prefix: '',
                    custom_domain: null,
                    sending_address: null,
                    from_email: defaultFromEmail,
                }));

            // For the custom domain method, use the entered sending addresses
            } else {
                // Get the sending addresses for the division. When the divisions feature isn't
                // active, there's only one (implicit) division for the whole environment, so
                // every sending address belongs to it regardless of its stored division id
                const availableAddresses = activeFeatures.includes('divisions')
                    ? sendingAddresses.filter(address => !address.deleted && address.division === (effectiveDivisionObject?.id ?? null))
                    : sendingAddresses.filter(address => !address.deleted);

                // Set the available sending addresses to be selected
                if (availableAddresses.length > 0) {
                    setFilteredSendingAddresses(availableAddresses);

                    // Reset the from email and selected sending address when the division changes
                    setData(prev => {
                        if (!prev) return prev;

                        // Do not change when the current sending address links to the same division
                        const currentAddressSameDivision = availableAddresses.some(address => address.id === prev.sending_address);
                        if (currentAddressSameDivision) {
                            return prev;
                        }

                        // Otherwise reset the from email and selected sending address
                        return {
                            ...prev,
                            from_prefix: '',
                            custom_domain: null,
                            sending_address: null,
                            from_email: '',
                        };
                    });

                // Otherwise use the default from email if no sending addresses are set
                } else {
                    setFilteredSendingAddresses([]);

                    setData(prev => ({
                        ...prev!,
                        from_prefix: '',
                        custom_domain: null,
                        sending_address: null,
                        from_email: defaultFromEmail,
                    }));
                }
            }
        }
    }, [data?.division, data?.id, sendingAddresses, divisions, environmentSettings, activeFeatures]);
    
    // Filter the connected features which are active in the environment
    const filteredConnectedFeatureOptions = connectedFeatureOptions.reduce((filteredItems, option) => {
        
        // Filter if the feature is active in the environment
        const isActive = activeFeatures.includes(option.value);

        // Filter if the feature is active in the role of the user
        const isRoleSpecific = roleFeatures.includes(option.value);
    
        // Check if the feature is enabled
        const enabled = (
            isRoleSpecific
                ? isActive && allowedFeatures.includes(option.value)
                : isActive 

        // Always add appointments (it is not a package feature)
        ) || option.value === 'appointments';

        // If its enabled, add it to the filtered items
        if (enabled) {
            filteredItems.push(option);
        }

        return filteredItems;
    }, [] as typeof connectedFeatureOptions);

    // Contact templates have no status to trigger on, so they are only sent manually
    const allowsAutomaticSending = data?.connected_feature !== 'contacts';

    // Filter the status options on the selected connected feature
    const filteredTriggerStatusOptions = data?.connected_feature
        ? triggerStatusOptionsMap[data.connected_feature] || []
        : [];

    // Filter out the before timing options based on the template type
    const filteredTriggerTimingOptions = data?.template_type === 'appointment_reminder'
        ? triggerTimingOptions.filter(option => option.value === 'before')
        : triggerTimingOptions.filter(option => option.value !== 'before')

    // Handle connected feature change and reset the send to
    const handleConnectedFeatureChange = (selectedValue: string) => {
        setSelectedSendTo([]);
        setData(prev => ({
            ...prev!, 
            connected_feature: selectedValue,
            send_to: [],
        }))
    };

    // Handle division change
    const handleDivisionChange = (selectedValue: string) => {
        const selectedDivision = divisions.find(division => division.id === parseFloat(selectedValue));
        setData(prev => ({ ...prev!, division: selectedDivision ? selectedDivision.id : null }));
    };

    // Handle trigger timing change. Reset offset if selected value is immediate
    const handleTriggerTimingChange = (selectedValue: string) => {
        if (selectedValue === 'immediate') {
            setData(prev => ({
                ...prev!, 
                trigger_timing: 'immediate',
                trigger_offset_value: null,
                trigger_offset_unit: null,
            }))
        } else {
            setData(prev => ({
                ...prev!, 
                trigger_timing: selectedValue
            }))
        }
    };

    return (
        <>
            <div className='card'>
                <h6 className='template-fields-header'>
                    {t('email.email_template.details_title')}
                </h6>
                {loading === 'show-loader' ? (
                    <div className='inline-form-loader'>
                        <div className="loader"></div>
                    </div>
                ) : loading === 'success' ? (
                    <div className='field-group'>
                        <div className='edit-mode'>
                            <label>
                                {t('email.email_template.name_label')}
                            </label>
                            <input
                                type="text"
                                id='name'
                                name='name'
                                placeholder={t('email.email_template.template_name_label')}
                                value={data?.name ?? ''}
                                onChange={(e) => setData(prev => ({...prev!, name: e.target.value}))}
                                className={`text ${(errorMessages['name']) ? 'is-invalid' : ''}`}
                                autoFocus={true}
                            />
                            {errorMessages['name'] && (
                                <div className='error-message'>
                                    {t(errorMessages['name'])}
                                </div>
                            )}
                        </div>
                        <div className='edit-mode'>
                            <label>
                                {t('email.email_template.connected_feature_label')}
                            </label>
                            <Dropdown<{ value: string, name: string }>
                                options={filteredConnectedFeatureOptions}
                                id='connected_feature'
                                name='connected_feature'
                                disabled_selected={t('email.email_template.connected_feature_disabled_selected')}
                                selectedOption={connectedFeatureOptions.find(option => option.value === data?.connected_feature)}
                                onChange={(selectedValue) => handleConnectedFeatureChange(selectedValue)}
                                selectionFormat={(option) => `${option.name}`}
                                optionFormat='name'
                                showSearch={false}
                                allowNoneOption={false}  
                                isInvalid={errorMessages['connected_feature']}    
                                disabled={!!data?.id || (data?.template_type !== 'blank_template' && templateType !== 'blank_template')}                       
                            />
                            {errorMessages['connected_feature'] && (
                                <div className='error-message'>
                                    {t(errorMessages['connected_feature'])}
                                </div>
                            )}
                        </div>
                        {activeFeatures.includes('divisions') && (
                            <div className='edit-mode'>
                                <label>
                                    {t('email.email_template.division_label')}
                                </label>
                                <Dropdown<Division>
                                    options={divisions?.filter(division => division.is_active) || []}
                                    id='division'
                                    name='division'
                                    disabled_selected={t('email.email_template.division_disabled_selected')}
                                    selectedOption={divisions.find(division => data?.division === division.id)}
                                    onChange={(selectedValue) => handleDivisionChange(selectedValue)}
                                    selectionFormat={(option) => `${option.name}`}
                                    optionFormat='name'
                                    showSearch={false}
                                    allowNoneOption={false}
                                    isInvalid={errorMessages['division']} 
                                />
                                {errorMessages['division'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['division'])}
                                    </div>
                                )}
                            </div>
                        )}
                        {activeFeatures.includes('email_layouts') && (
                            <div className='edit-mode'>
                                <label>
                                    {t('email.email_template.layout_label')}
                                </label>
                                <Dropdown<EmailLayout>
                                    options={filteredEmailLayouts?.filter(layout => !layout.deleted) || []}
                                    id='layout'
                                    name='layout'
                                    disabled_selected={t('email.email_template.select_layout_disabled_selected')}
                                    selectedOption={filteredEmailLayouts.find(layout => data?.layout === layout.id)}
                                    onChange={(selectedValue) => setData(prev => ({...prev!, layout: parseFloat(selectedValue)}))}
                                    selectionFormat={(option) => `${option.name}`}
                                    optionFormat='name'
                                    showSearch={false}
                                    allowNoneOption={false}
                                    isInvalid={errorMessages['layout']} 
                                />
                                {errorMessages['layout'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['layout'])}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
            {/* <div className='card'>
                <h6 className='template-fields-header'>
                    {t('email.email_template.conditions_title')}
                </h6>
                {loading === 'show-loader' ? (
                    <div className='inline-form-loader'>
                        <div className="loader"></div>
                    </div>
                ) : loading === 'success' ? (
                    <div className='field-group'>
                        <div className='radio-field'>
                            <label>
                                {t('email.email_template.send_type_label')}
                            </label>
                            <div className='radio-options-container'>
                                <div className='radio-option'>
                                    <input
                                        type="radio"
                                        id='send_type_manual'
                                        name='send_method'
                                        value='manual'
                                        checked={data?.send_method === 'manual'}
                                        onChange={(event) => setData(prev => ({
                                            ...prev!, 
                                            send_method: event.target.value,
                                            trigger_timing: null,
                                            trigger_status: null,
                                            trigger_offset_unit: null,
                                            trigger_offset_value: null,
                                        }))}
                                    />
                                    <label htmlFor='send_type_manual' className="radio-label">
                                        {t('email.email_template.send_type.manual')}
                                    </label>
                                </div>
                                {allowsAutomaticSending && (
                                    <div className='radio-option'>
                                        <input
                                            type="radio"
                                            id='send_type_automatically'
                                            name='send_method'
                                            value='automatically'
                                            checked={data?.send_method === 'automatically'}
                                            onChange={(e) => setData(prev => ({...prev!, send_method: e.target.value}))}
                                        />
                                        <label htmlFor='send_type_automatically' className="radio-label">
                                            {t('email.email_template.send_type.automatically')}
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>                        
                        {data?.send_method === 'automatically' && (
                            <div>
                                <p>De e-mail wordt automatisch verstuurd als wordt voldaan aan de volgende voorwaarde:</p>
                                {data?.connected_feature !== null ? (
                                    (templateType === null || templateType != 'appointment_reminder') && (              
                                        <>  
                                            <div className='condition-item'>
                                                <p>
                                                    Als de {t(`email.email_template.connected_feature.singular.${data?.connected_feature}`)} de status
                                                </p>
                                                <Dropdown<{ value: string, name: string }>
                                                    options={filteredTriggerStatusOptions}
                                                    id='trigger_status'
                                                    name='trigger_status'
                                                    disabled_selected={t('email.email_template.trigger_status_disabled_selected')}
                                                    selectedOption={filteredTriggerStatusOptions.find(option => option.value === data?.trigger_status)}
                                                    onChange={(selectedValue) => setData(prev => ({...prev!, trigger_status: selectedValue}))}
                                                    selectionFormat={(option) => `${option.name}`}
                                                    optionFormat='name'
                                                    showSearch={false}
                                                    allowNoneOption={false}                            
                                                />
                                                <p>
                                                    heeft, verzend deze e-mail dan
                                                </p>
                                                <Dropdown<{ value: string, name: string }>
                                                    options={filteredTriggerTimingOptions}
                                                    id='trigger_timing'
                                                    name='trigger_timing'
                                                    disabled_selected={t('email.email_template.trigger_timing_disabled_selected')}
                                                    selectedOption={triggerTimingOptions.find(option => option.value === data?.trigger_timing)}
                                                    onChange={(selectedValue) => handleTriggerTimingChange(selectedValue)}
                                                    selectionFormat={(option) => `${option.name}`}
                                                    optionFormat='name'
                                                    showSearch={false}
                                                    allowNoneOption={false}                            
                                                />
                                            </div>
                                            {data?.trigger_timing === 'delay' && (
                                                <div className='condition-item'>
                                                    <p>
                                                        Verzend deze e-mail als de {t(`email.email_template.connected_feature.singular.${data?.connected_feature}`)} zich na
                                                    </p>
                                                    <input
                                                        type="text"
                                                        id='trigger_offset_value'
                                                        name='trigger_offset_value'
                                                        placeholder={t('email.email_template.trigger_offset_value_placeholder')}
                                                        value={data?.trigger_offset_value ?? ''}
                                                        onChange={(e) => setData(prev => ({...prev!, trigger_offset_value: e.target.value}))}
                                                        className={`text trigger-offset-value ${(errorMessages['trigger_offset_value']) ? 'is-invalid' : ''}`}
                                                    />
                                                    <Dropdown<{ value: string, name: string }>
                                                        options={triggerOffsetUnitOptions}
                                                        id='trigger_offset_unit'
                                                        name='trigger_offset_unit'
                                                        disabled_selected={t('email.email_template.trigger_offset_unit_disabled_selected')}
                                                        selectedOption={triggerOffsetUnitOptions.find(option => option.value === data?.trigger_offset_unit)}
                                                        onChange={(selectedValue) => setData(prev => ({...prev!, trigger_offset_unit: selectedValue}))}
                                                        selectionFormat={(option) => `${option.name}`}
                                                        optionFormat='name'
                                                        showSearch={false}
                                                        allowNoneOption={false}                            
                                                    />
                                                    <p>
                                                        nog in deze status bevindt.
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )
                                ) : (
                                    <p>Selecteer de functie waarvoor je het e-mail sjabloon wil toevoegen, voordat je de voorwaarde kunt instellen.</p>
                                )}
                                {templateType === 'appointment_reminder' && (
                                    <div className='condition-item'>
                                        <p>
                                            Verzend de e-mail afspraak herinnering 
                                        </p>
                                        <input
                                            type="text"
                                            id='trigger_offset_value'
                                            name='trigger_offset_value'
                                            placeholder={t('email.email_template.trigger_offset_value_placeholder')}
                                            value={data?.trigger_offset_value ?? ''}
                                            onChange={(e) => setData(prev => ({...prev!, trigger_offset_value: e.target.value}))}
                                            className={`text trigger-offset-value ${(errorMessages['trigger_offset_value']) ? 'is-invalid' : ''}`}
                                        />
                                        <Dropdown<{ value: string, name: string }>
                                            options={triggerOffsetUnitOptions}
                                            id='trigger_offset_unit'
                                            name='trigger_offset_unit'
                                            disabled_selected={t('email.email_template.trigger_offset_unit_disabled_selected')}
                                            selectedOption={triggerOffsetUnitOptions.find(option => option.value === data?.trigger_offset_unit)}
                                            onChange={(selectedValue) => setData(prev => ({...prev!, trigger_offset_unit: selectedValue}))}
                                            selectionFormat={(option) => `${option.name}`}
                                            optionFormat='name'
                                            showSearch={false}
                                            allowNoneOption={false}
                                        />
                                        <p>
                                            voor de aanvang van de afspraak.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}    
                    </div>
                ) : null}
            </div> */}
        </>
    );
};

export default EmailTemplateFormBaseFields;